import type { TAwardsPolicy } from '@schemas'
import { SPECIAL_AWARD_KEYS, type SpecialAwardKey } from './special-awards'

export type AwardsEligibilityFlags = Record<SpecialAwardKey, boolean>

export const DEFAULT_AWARDS_ELIGIBILITY: AwardsEligibilityFlags = {
  best_in_show: true,
  grand_champion: true,
  reserve_grand: true,
}

export function extractBestInDivisionEnabled(policy: TAwardsPolicy | null | undefined): boolean {
  if (!policy) return false
  return Boolean(policy.bestInDivisionEnabled)
}

export function normalizeAwardsEligibility(
  policy: TAwardsPolicy | null | undefined
): AwardsEligibilityFlags {
  const baseline: AwardsEligibilityFlags = { ...DEFAULT_AWARDS_ELIGIBILITY }
  const eligibility = policy?.awardEligibility
  if (!eligibility) {
    return baseline
  }

  for (const key of SPECIAL_AWARD_KEYS) {
    const record = eligibility[key]
    if (record && typeof record.staffEligible === 'boolean') {
      baseline[key] = record.staffEligible
    }
  }

  return baseline
}

export function createAwardsPolicyPayload(
  nextFlags: AwardsEligibilityFlags,
  currentPolicy: TAwardsPolicy | null | undefined,
  nextBestInDivisionEnabled: boolean
): TAwardsPolicy | null | undefined {
  const currentFlags = normalizeAwardsEligibility(currentPolicy)
  const bestInDivisionCurrent = extractBestInDivisionEnabled(currentPolicy)
  const flagsChanged = SPECIAL_AWARD_KEYS.some(key => nextFlags[key] !== currentFlags[key])
  const bestChanged = nextBestInDivisionEnabled !== bestInDivisionCurrent

  const hasRestrictedAward = SPECIAL_AWARD_KEYS.some(key => nextFlags[key] === false)
  const nextPolicy: TAwardsPolicy = {
    ...(currentPolicy?.heroAwardEnabled !== undefined
      ? { heroAwardEnabled: currentPolicy.heroAwardEnabled }
      : {}),
  }

  if (hasRestrictedAward) {
    nextPolicy.awardEligibility = SPECIAL_AWARD_KEYS.reduce(
      (acc, key) => {
        acc[key] = { staffEligible: nextFlags[key] }
        return acc
      },
      {} as Record<SpecialAwardKey, { staffEligible: boolean }>
    )
  }

  const hadBestConfig = currentPolicy?.bestInDivisionEnabled !== undefined
  const includeBestField =
    bestChanged || hasRestrictedAward || (flagsChanged && !hasRestrictedAward && hadBestConfig)
  if (includeBestField && (bestChanged || hadBestConfig || nextBestInDivisionEnabled)) {
    nextPolicy.bestInDivisionEnabled = nextBestInDivisionEnabled
  }

  if (Object.keys(nextPolicy).length === 0) {
    if (flagsChanged || bestChanged) {
      return null
    }
    return undefined
  }

  return nextPolicy
}
